/**
 * Template portfolio LLM tasks (docs/tech/12-template-portfolio-creation.md §7):
 * AI 分析分类 and AI 补充生成. Both run as BullMQ jobs; the UI polls the task status.
 */
import { z } from "zod";

/** What the LLM may answer per keyword (doc §7.1); code maps it onto keyword pools. */
export const TemplateKeywordAiCategorySchema = z.enum([
  "generic",
  "competitor_brand",
  "misspelling",
  "non_english",
  "negative_candidate",
  "discard",
]);
export type TemplateKeywordAiCategory = z.infer<typeof TemplateKeywordAiCategorySchema>;

/** Pools that offer「AI 补充生成」(doc §7.2). */
export const TemplateKeywordGeneratePoolSchema = z.enum([
  "competitor_brand",
  "misspelling",
  "non_english",
  "negative",
]);
export type TemplateKeywordGeneratePool = z.infer<typeof TemplateKeywordGeneratePoolSchema>;

export const TEMPLATE_KEYWORD_GENERATE_MAX_COUNT = 200;

/** Keywords per AI classification call; small enough that the reply is not truncated. */
export const TEMPLATE_KEYWORD_CLASSIFY_BATCH_SIZE = 100;

export const TemplateKeywordGenerateInputSchema = z.object({
  pool: TemplateKeywordGeneratePoolSchema,
  count: z.number().int().min(1).max(TEMPLATE_KEYWORD_GENERATE_MAX_COUNT).default(100),
});
export type TemplateKeywordGenerateInput = z.infer<typeof TemplateKeywordGenerateInputSchema>;

/**
 * `idle` = never run (or expired). `partial` = classification finished but some batches
 * failed; the remaining keywords stay `unclassified` and POST .../classify/retry reruns them.
 */
export const TemplateAiTaskStatusSchema = z.enum([
  "idle",
  "queued",
  "running",
  "completed",
  "failed",
  "partial",
]);
export type TemplateAiTaskStatus = z.infer<typeof TemplateAiTaskStatusSchema>;

export interface TemplateKeywordClassifyTask {
  status: TemplateAiTaskStatus;
  batchesTotal: number;
  /** Finished batches, failed ones included. */
  batchesDone: number;
  batchesFailed: number;
  keywordsClassified: number;
}

export interface TemplateKeywordGenerateTask {
  pool: TemplateKeywordGeneratePool;
  /** Never `partial`: generation is one LLM call. */
  status: TemplateAiTaskStatus;
  requested: number;
  /** Keywords appended after validation / dedupe; 0 until completed. */
  generated: number;
}

/** GET .../generate answers every pool at once so the keyword page polls one endpoint. */
export type TemplateKeywordGenerateTasks = Record<
  TemplateKeywordGeneratePool,
  TemplateKeywordGenerateTask
>;
